const TORCH_FILE_REFERENCE_TRAILER_PATTERN =
  /\n\nFull results saved to: \S+\nUse read_file to access the complete output\./g;

const TORCH_FILE_REFERENCE_PATH_PATTERN = /Full results saved to:\s*(\S+)/;

const INTERNAL_FILESYSTEM_ROOTS = [
  '/workspace',
  '/artifacts',
  '/skills',
  '/.subagent-skills',
  '/custom-skills',
  '/testing-instructions',
  '/memories',
] as const;

const INTERNAL_FILESYSTEM_ROOT_SOURCE =
  '/(?:workspace|artifacts|skills|\\.subagent-skills|custom-skills|testing-instructions|memories)';

const INTERNAL_PATH_BOUNDARY = String.raw`(^|[\s("'\`:])`;

const INTERNAL_PATH_BODY = `${INTERNAL_FILESYSTEM_ROOT_SOURCE}(?:\\/[^\\s"'\\\`)\\]}>|,;:]*)`;

const TERMINATED_INTERNAL_PATH_PATTERN = new RegExp(
  `${INTERNAL_PATH_BOUNDARY}${INTERNAL_PATH_BODY}(?=[\\s"'\\\`)\\]}>|,;:])`,
  'g',
);

const INTERNAL_PATH_PATTERN = new RegExp(
  `${INTERNAL_PATH_BOUNDARY}${INTERNAL_PATH_BODY}`,
  'g',
);

const TRAILING_PATH_TOKEN_PATTERN =
  /(?:^|[\s("'`:])(\/[^\s"'`)\]}>|,;:]*)$/;

export function buildTorchFileReference({
  body,
  fileKey,
}: {
  body: string;
  fileKey: string;
}): string {
  return `${body}\n\nFull results saved to: ${fileKey}\nUse read_file to access the complete output.`;
}

export function redactUserVisibleInternalPaths(text: string): string {
  return text.replace(INTERNAL_PATH_PATTERN, '$1');
}

export function stripTorchFileReference(text: string): string {
  return redactUserVisibleInternalPaths(
    text.replace(TORCH_FILE_REFERENCE_TRAILER_PATTERN, ''),
  );
}

export function trailingInternalPathHoldLength(text: string): number {
  const token = TRAILING_PATH_TOKEN_PATTERN.exec(text)?.[1];

  if (!token) {
    return 0;
  }

  const open = INTERNAL_FILESYSTEM_ROOTS.some(
    (root) =>
      root.startsWith(token) ||
      (token.startsWith(root) &&
        (token.length === root.length || token[root.length] === '/')),
  );

  return open ? token.length : 0;
}

export function pushUserVisibleText({
  pending,
  delta,
}: {
  pending: string;
  delta: string;
}): { pending: string; emit: string } {
  const combined = `${pending}${delta}`.replace(
    TERMINATED_INTERNAL_PATH_PATTERN,
    '$1',
  );
  const hold = trailingInternalPathHoldLength(combined);

  return {
    emit: combined.slice(0, combined.length - hold),
    pending: combined.slice(combined.length - hold),
  };
}

export function flushUserVisibleText(pending: string): string {
  return redactUserVisibleInternalPaths(pending);
}

export function parseTorchFileReferencePath(text: string): string | undefined {
  return TORCH_FILE_REFERENCE_PATH_PATTERN.exec(text)?.[1];
}
